<script lang="ts" setup>
import type { IconName } from '~/components/ui/Icon.vue';

export interface MapMarker {
  id: number;
  latitude: number;
  longitude: number;
  icon: IconName;
  selected?: boolean;
}

// Emits
const emit = defineEmits<{
  'click-marker': [MapMarker];
}>();

// Props
withDefaults(
  defineProps<{
    height?: string;
    latitude?: number;
    longitude?: number;
    zoom?: number;
    mapClassName?: string;
    markers?: MapMarker[];
    loading?: boolean;
  }>(),
  {
    height: '400px',
    latitude: 46.603354,
    longitude: 1.888334,
    zoom: 5,
    mapClassName: '',
    markers: undefined,
    loading: false,
  },
);

// Data
const selectedMarker = ref<MapMarker>();

// Methods
const selectMarker = (marker: MapMarker) => {
  selectedMarker.value = marker;
  emit('click-marker', marker);
};
</script>
<template>
  <div :class="`h-[${height}] w-full`" class="relative">
    <mapbox-map
      map-id="map"
      :class="`h-full w-full ${mapClassName}`"
      :options="{
        style: 'mapbox://styles/mapbox/light-v11',
        center: [longitude, latitude],
        zoom: zoom,
      }"
    >
      <template v-if="markers">
        <mapbox-default-marker
          v-for="marker in markers"
          :key="marker.id"
          :marker-id="`marker-${marker.id}`"
          :lnglat="[marker.longitude, marker.latitude]"
        >
          <template #marker>
            <div
              class="rounded-full shadow-md p-2 cursor-pointer bg-white"
              :class="[
                marker.selected ? 'border border-1 border-emerald-600' : '',
                selectedMarker?.id === marker.id ? '!bg-blue-500 text-white' : '',
              ]"
              @click="selectMarker(marker)"
            >
              <ui-icon :name="marker.icon" class="size-6" :class="selectedMarker?.id === marker.id ? 'text-white' : 'text-emerald-600'" />
            </div>
          </template>
        </mapbox-default-marker>
      </template>
    </mapbox-map>

    <div v-if="loading" class="absolute top-0 left-0 w-full h-full bg-black/50 z-10 flex items-center justify-center">
      <ui-loader class="w-5 h-5" />
    </div>
  </div>
</template>
